import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-self-control",
  title: "10 Bible verses about self-control, and what they mean",
  h1: "Bible verses about self-control",
  description:
    "Ten passages on self-control from the World English Bible, each with a note on what it actually says — including the famous ones that do not mean what they are quoted to mean.",
  targets: [
    "bible verses about self control",
    "scripture on self discipline",
    "what does the bible say about self control",
  ],
  published: "2026-08-26",
  standfirst:
    "Scripture talks about self-control far more often than it talks about willpower, and it almost never treats the two as the same thing.",
  takeaways: [
    "Self-control appears in the New Testament as a fruit of the Spirit rather than as an achievement — Galatians 5:23 lists it alongside love and patience, all of them grown rather than summoned.",
    "The most-quoted image, the broken city walls of Proverbs 25:28, is about defencelessness rather than about shame: an unwalled city is not wicked, it is open to anything that arrives.",
    "Paul's language in 1 Corinthians 9 is athletic — training, not straining — which is a different model from the one most people apply to their own habits.",
    "Every passage below is quoted from the World English Bible, which is in the public domain, so it can be printed in full rather than paraphrased.",
  ],
  intro: [
    {
      type: "p",
      text: "Most people looking for verses on self-control are looking for them at a particular moment: after something has gone the way it always goes, at the end of a day that was supposed to be different. That is worth naming, because it changes which passages are useful. A verse read as an accusation does nothing except add a layer of guilt to a habit that was already running, and guilt is not an ingredient in change — it is usually the thing that sends people back to the habit.",
    },
    {
      type: "p",
      text: "So the notes below are about what each passage actually claims. Several of them are quoted far more often than they are read, and two of the most famous say something noticeably gentler than their reputation. Where a verse is about training rather than force, or about protection rather than performance, the note says so.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain. Where a passage is trimmed, the ellipsis is marked.",
    },
  ],
  verses: [
    {
      verse: {
        text: "But the fruit of the Spirit is love, joy, peace, patience, kindness, goodness, faith, gentleness, and self-control.",
        ref: "Galatians 5:22–23",
      },
      note: "The single most important passage on the subject, and the one whose grammar does the work: fruit, singular, of which self-control is one part. Fruit is not manufactured on demand — it is what a healthy thing produces over a season. That does not remove effort, but it relocates it: the effort goes into what is being fed rather than into the moment of temptation itself.",
    },
    {
      verse: {
        text: "Like a city that is broken down and without walls is a man whose spirit is without restraint.",
        ref: "Proverbs 25:28",
      },
      note: "Usually quoted as a rebuke; the image is actually about exposure. A city without walls has not done anything wrong. It is simply unable to decide what comes in, which is exactly what it feels like to reach for a phone before you have decided to. The remedy the metaphor implies is a structure, not an apology.",
    },
    {
      verse: {
        text: "For God didn't give us a spirit of fear, but of power, love, and self-control.",
        ref: "2 Timothy 1:7",
      },
      note: "Written to a young man who appears to have been frightened of his own responsibilities. The three things named as gifts are the three that fear removes first, and self-control sits in that company — meaning it is described here as a capacity given rather than a standard demanded.",
    },
    {
      verse: {
        text: "Every man who strives in the games exercises self-control in all things … I beat my body and bring it into submission.",
        ref: "1 Corinthians 9:25, 27",
      },
      note: "The whole passage is athletic. An athlete's discipline is not a series of heroic refusals; it is a training programme, boring and repeated, arranged so that the difficult thing becomes possible later. Almost everyone who quotes this applies it as strain in the moment, which is the opposite of what an athlete does.",
    },
    {
      verse: {
        text: "One who is slow to anger is better than the mighty; one who rules his spirit, than he who takes a city.",
        ref: "Proverbs 16:32",
      },
      note: "A deliberately absurd comparison, and that is the point of it: the ancient world's highest visible achievement, set below a private one nobody will ever see you perform. It is also an honest measure of cost, because ruling your own spirit takes longer than a siege.",
    },
    {
      verse: {
        text: "“All things are lawful for me,” but not all things are expedient. “All things are lawful for me,” but I will not be brought under the power of anything.",
        ref: "1 Corinthians 6:12",
      },
      note: "Paul quotes a slogan back at the people using it. The test he substitutes is not whether a thing is permitted but whether it has power over you — which is a question about pattern rather than about morality, and it is the one that makes sense of habits that are entirely harmless in a single instance.",
    },
    {
      verse: {
        text: "Adding on your part all diligence, in your faith supply … knowledge; and in knowledge, self-control; and in self-control, perseverance.",
        ref: "2 Peter 1:5–6",
      },
      note: "A chain, and the order is doing something. Self-control comes after knowledge — you cannot govern a pattern you have not noticed — and before perseverance, which is what turns a good week into a changed year. Most attempts at change skip the first link and wonder why the third never arrives.",
    },
    {
      verse: {
        text: "Turn my eyes away from looking at worthless things. Revive me in your ways.",
        ref: "Psalm 119:37",
      },
      note: "A request rather than a resolution, and specifically a request about attention. The psalmist asks for his eyes to be turned rather than promising to look away, which is a strikingly accurate description of how attention actually works — it is captured, not chosen, and the help asked for is help at the point of capture.",
    },
    {
      verse: {
        text: "Keep your heart with all diligence, for out of it is the wellspring of life.",
        ref: "Proverbs 4:23",
      },
      note: "The verb is a guarding word — the kind used of a watchman. It assumes something worth protecting and something that will try to get in, and it assumes the guarding is continuous rather than heroic. A watchman's work is almost entirely uneventful, which is the part that gets left out.",
    },
    {
      verse: {
        text: "Don't be conformed to this world, but be transformed by the renewing of your mind.",
        ref: "Romans 12:2",
      },
      note: "Both verbs are passive: be conformed, be transformed. Something is going to shape you, and the only question is what. That is a more useful frame than willpower for anything involving a feed, because a feed does not require your consent to shape what you find normal — it only requires your time.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "how-to-use-these",
      text: "How to actually use a list like this",
    },
    {
      type: "p",
      text: "Not by reading all ten. A collection is for choosing from, and the way these passages do their work is by being available at the moment they apply, which is never the moment you are sitting down reading a page about them. Pick one. Read the surrounding chapter once so you know what it is doing there. Then keep it somewhere you will meet it again — written down, memorised, or set as the thing you see when you reach for the phone.",
    },
    {
      type: "p",
      text: "That last option is the whole design of {Bible-Shield}: the apps you have chosen close at the times you set, and opening one asks for a short prayer first rather than showing you a countdown. A verse encountered at the moment of reaching is doing something a verse read on a Sunday afternoon cannot.",
      links: { "Bible-Shield": "/" },
    },
    {
      type: "h2",
      id: "a-prayer",
      text: "A short prayer from these passages",
    },
    {
      type: "p",
      text: "Father, you did not give me a spirit of fear. Turn my eyes from what is worthless and revive me in your ways. Where my walls are down, build them; where I have been brought under the power of a small thing, break it. Not today only. Amen.",
    },
    {
      type: "note",
      text: "If one of these lands, read the chapter it comes from rather than collecting nine more verses. Depth in one passage outperforms a longer list every time.",
    },
  ],
  faq: [
    {
      q: "Is self-control a fruit of the Spirit or something I have to work at?",
      a: "Galatians 5 calls it fruit, and the New Testament also tells people to make an effort — 2 Peter 1:5 says to add diligence. The two are not in competition: fruit describes where the capacity comes from, diligence what you do with it. Neither describes summoning it from nothing at the moment of temptation.",
    },
    {
      q: "Which translation are these from?",
      a: "The World English Bible, a public-domain translation, so the passages can be quoted in full. Any mainstream translation will render them similarly; the wording differs, the meaning does not.",
    },
    {
      q: "What is the best single verse on self-control?",
      a: "Proverbs 25:28 if you want the diagnosis, and Galatians 5:22–23 if you want the remedy. The first explains what is happening when you reach without deciding; the second says where the capacity to stop actually comes from.",
    },
  ],
  related: [
    "screen-time-limits-dont-work",
    "christian-app-blocker-guide",
    "phone-addiction-bible-verses",
  ],
  relatedTopics: ["bible-verses-about-temptation", "bible-verses-about-time"],
};
